import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiHome, FiTool, FiBriefcase, FiSettings, FiCheckCircle } = FiIcons;

function Services() {
  const services = [
    {
      icon: FiHome,
      title: 'Residential Construction',
      description: 'Custom homes, additions, and residential projects built to your specifications.',
      features: ['Custom Home Building', 'Home Additions', 'Kitchen & Bath Remodeling', 'Basement Finishing'],
      image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
    },
    {
      icon: FiBriefcase,
      title: 'Commercial Construction',
      description: 'Office buildings, retail spaces, and commercial facilities for your business needs.',
      features: ['Office Buildings', 'Retail Spaces', 'Warehouses', 'Industrial Facilities'],
      image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
    },
    {
      icon: FiTool,
      title: 'Renovation & Remodeling',
      description: 'Transform your existing space with our comprehensive renovation services.',
      features: ['Interior Renovations', 'Exterior Upgrades', 'Structural Modifications', 'Historic Restoration'],
      image: 'https://images.unsplash.com/photo-1581858726788-75bc0f6a952d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
    },
    {
      icon: FiSettings,
      title: 'Project Management',
      description: 'End-to-end project management ensuring timely and budget-conscious completion.',
      features: ['Project Planning', 'Budget Management', 'Timeline Coordination', 'Quality Control'],
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Construction Services | Residential & Commercial Building | BuildCorp</title>
        <meta name="description" content="BuildCorp offers comprehensive construction services including residential homes, commercial buildings, renovations, and project management. Professional quality guaranteed." />
        <meta name="keywords" content="construction services, residential building, commercial construction, renovation services, project management, custom homes" />
      </Helmet>

      <div className="pt-20">
        {/* Hero Section */}
        <section className="bg-gray-50 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <h1 className="text-4xl md:text-5xl font-bold text-secondary mb-6">
                Our <span className="text-primary">Services</span>
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Comprehensive construction solutions tailored to meet your unique needs and exceed your expectations
              </p>
            </motion.div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-20">
              {services.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                    index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
                  }`}
                >
                  <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                    <div className="flex items-center mb-4">
                      <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center mr-4">
                        <SafeIcon icon={service.icon} className="w-6 h-6 text-white" />
                      </div>
                      <h2 className="text-3xl font-bold text-secondary">{service.title}</h2>
                    </div>
                    <p className="text-gray-600 mb-6 text-lg">{service.description}</p>
                    <ul className="space-y-3">
                      {service.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-center">
                          <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-primary mr-3" />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className={index % 2 === 1 ? 'lg:col-start-1' : ''}>
                    <img
                      src={service.image}
                      alt={service.title}
                      className="rounded-lg shadow-lg w-full h-80 object-cover"
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-primary py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl font-bold text-white mb-4">
                Ready to Start Your Project?
              </h2>
              <p className="text-xl text-orange-100 mb-8 max-w-2xl mx-auto">
                Let's discuss your construction needs and create something amazing together
              </p>
              <button className="bg-white text-primary px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors duration-200">
                Get Free Consultation
              </button>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
}

export default Services;